'use client'
import { cn, Select, SelectItem } from "@heroui/react"
import { ComponentProps, DetailedHTMLProps, HTMLAttributes, HTMLInputTypeAttribute, MutableRefObject, PropsWithChildren, ReactNode, RefObject } from "react"

export function anyToString(arg: any) {
  if (arg === undefined || arg === null) return ''
  return arg.toString()
}

export type InputProps = {
  desc?: string
  label?: ReactNode
  id?: string | number
  value?: string | number
  inputRef?: MutableRefObject<HTMLInputElement>
  placeholder?: string
  onChange?: (val: string) => any
  classNames?: ClassNames
  onBlur?: () => void
  onFocus?: () => void
  isRequired?: boolean

  isInvalid?: boolean
  errorMessage?: string
  hideLabel?: boolean

} & WrapperExtends & PropsWithChildren

export type WrapperExtends = {
  StartWrapperContent?: ReactNode
  EndWrapperContent?: ReactNode
}

export type ClassNames = {
  inputBox?: string
  base?: string
  label?: string
  input?: string
  inputWrapper?: string
}

type ResponsiveWrapperProps =
  PropsWithChildren &
  { classNames?: Pick<ClassNames, 'base' | 'label' | 'inputBox' | 'inputWrapper'> } &
  Pick<InputProps, 'desc' | 'label' | 'id' | 'isInvalid' | 'errorMessage' | 'isRequired' | 'hideLabel'> & WrapperExtends



export const ResponsiveWrapper = ({
  children,
  StartWrapperContent,
  EndWrapperContent,
  classNames = {},
  desc,
  label,
  id,
  isInvalid,
  errorMessage,
  isRequired,
  hideLabel,
  ...reset }: ResponsiveWrapperProps) => {
  const inputArea = (
    <div className={cn("flex-1 xl:max-w-[490px] w-full", hideLabel && "!max-w-none", classNames.inputWrapper)}>
      {StartWrapperContent}
      <div
        id={id}
        className={cn(
          "relative overflow-hidden border-[1.5px] rounded-[8px]",
          "hover:border-gradient-purple2/40 focus-within:!border-gradient-purple2/70 text-theme-primary/50 border-theme-primary/10",
          "group-data-[invalid=true]:!border-theme-light-red",
          "group-data-[invalid=true]:!shadow-theme-light-red/20 group-data-[invalid=true]:shadow-lg",
          "transition-shadow scroll-mt-10",
          classNames.inputBox)}>
        {children}
      </div>
      {EndWrapperContent}
      {isInvalid &&
        <p className="mt-[12px] leading-[1.2] text-[14px] text-theme-light-red text-start">{errorMessage}</p>
      }
    </div>
  )

  if (hideLabel) {
    return (
      <div data-invalid={!!isInvalid} className={cn("group w-full", classNames.base)} {...reset}>
        {inputArea}
      </div>
    )
  }

  return (
    <div data-invalid={!!isInvalid} className={cn("group flex flex-col xl:flex-row gap-4 w-full justify-between", classNames.base)} {...reset}>
      <label className={cn("leading-[1] text-start font-bold", classNames.label)} htmlFor={id as string}>
        <div className="text-[16px] text-theme-primary">{label} {isRequired && <span className="opacity-40"> * </span>} </div>
        {desc && <p className="mt-[8px] text-[14px] max-w-[300px] !font-[300] text-t-subtitle leading-[1.2]">{desc}</p>}

      </label>
      {inputArea}
    </div>
  )
}

export const ResponsiveSelect = ({
  value,
  placeholder,
  onChange,
  inputRef,
  onBlur,
  classNames = {},
  list = [],
  ...reset }: InputProps & { list?: { id: string | number, label: string }[] }) => {

  return (
    <ResponsiveWrapper classNames={{ inputBox: "max-h-[40px]", ...classNames }} {...reset}>
      <Select
        id={reset.id as string}
        selectedKeys={value as string}
        onBlur={onBlur}
        radius="none"
        popoverProps={{
          radius: 'sm',
        }}
        aria-label={reset.label}
        onSelectionChange={(value) => onChange && onChange(value.currentKey!)}
        classNames={{
          trigger: '!bg-transparent',
          value: 'text-theme-primary/40 group-data-[has-value=true]:!text-theme-primary'
        }}
        placeholder={placeholder}>
        {
          list.map(item => (
            <SelectItem key={item.id}>{item.label}</SelectItem>
          ))
        }
      </Select>
    </ResponsiveWrapper>
  )
}

export const inputStyle = cn("block h-[40px] font-normal w-full py-[10px] px-[12px] focus:outline-none text-[14px] bg-transparent",
  "placeholder:text-theme-primary/40 text-theme-primary scroll-mt-10")

export const ResponsiveInput = ({
  value,
  placeholder,
  onChange,
  inputRef,
  onBlur,
  classNames = {},
  maxLength,
  ...reset }: InputProps & { maxLength?: number }) => {
  return (
    (<ResponsiveWrapper classNames={{ inputBox: "max-h-[40px]", ...classNames }} {...reset}>
      <input
        ref={inputRef as RefObject<HTMLInputElement | null>}
        id={reset.id as string}
        value={value}
        onBlur={onBlur}
        onChange={(ev) => onChange && onChange(ev.currentTarget.value)}
        className={cn(inputStyle,
          classNames?.input)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </ResponsiveWrapper>)
  );
}

